import _ from 'lodash'
import moment from '../../utils/moment'
import { getStartEndISOForTheDay, convertDateToLocalTimezone, toEntryDate } from '../../utils/date'
import { TimesheeIsAlreadyApprovedError } from '../../utils/errorClasses'
import { APPROVED } from '../../../constants/timesheetStatus'
import { log, logError } from '../../../utils/logger'
import { convertTimesheetToLocalDate } from '../../funnel/funnelUtils'
import queryClient from '../../../queryClient'
import { UNHANDLED_ENTRIES_QUERY_KEY } from '../../../hooks/queries/keys'

import * as timesheetService from '../../../entities/timesheet/service'

const getNewTimeEntryEvent = data => {
  const event = {
    ...data,
    start: isAlreadyUTC(data.start) ? data.start : moment(data.start).toLocalStringAsUTC(),
    end: isAlreadyUTC(data.end) ? data.end : moment(data.end).toLocalStringAsUTC(),
  }

  if (!_.isNil(data.originalEntryDate))
    event.originalEntryDate = toEntryDate(data.originalEntryDate)

  return event
}

const isAlreadyUTC = date => {
  return _.endsWith(date, 'Z')
}

export const getUpdatedTimeEntryEvent = data => {
  const timeEntryEvent = getNewTimeEntryEvent(data)
  return _.merge({}, timeEntryEvent, {
    _id: data._id,
    originalDurationInHours: data.originalDurationInHours,
    originalProjectId: data.originalProjectId,
    originalComment: data.originalComment,
    comment: data.comment,
    shouldUpdateComment: data.shouldUpdateComment,
  })
}

const getTimesheet = async ({ client, eventData, selectedDelegateId }) => {
  const { startISO, endISO } = getStartEndISOForTheDay(convertDateToLocalTimezone(eventData.start))

  const databaseTimesheets = await timesheetService.fetchTimesheets({
    client,
    startDate: startISO,
    endDate: endISO,
    shouldCreateIfNotExist: true,
    selectedDelegateId,
  })

  if (_.isNil(databaseTimesheets) || _.isEmpty(databaseTimesheets))
    throw new Error('Can not create entry without timesheet')

  const timesheets = convertTimesheetToLocalDate(databaseTimesheets)
  const [timesheet] = timesheets
  return timesheet
}

export const transformDataToValidEntry = async ({
  client,
  eventData,
  selectedDelegateId,
  timesheet: providedTimesheet,
}) => {
  const timesheet =
    providedTimesheet ?? (await getTimesheet({ client, eventData, selectedDelegateId }))
  const entryEvent = getNewTimeEntryEvent(eventData)

  if (timesheet.approvalStatus === APPROVED) {
    log(`Timesheet is already approved. Creating unhandled entry:`, entryEvent)
    queryClient.setQueryData(UNHANDLED_ENTRIES_QUERY_KEY, (prev = []) => [...prev, entryEvent])
    throw new TimesheeIsAlreadyApprovedError()
  }
  const entry = { ...entryEvent, repliconTimesheetId: timesheet.repliconId }
  return entry
}

export const dayKeyForEntry = eventData =>
  moment(convertDateToLocalTimezone(eventData.start)).format('YYYY-MM-DD')

export const resolveTimesheetsByDay = async ({ client, entriesData, selectedDelegateId }) => {
  const oneEntryPerDay = new Map()
  entriesData.forEach(eventData => {
    const key = dayKeyForEntry(eventData)
    if (!oneEntryPerDay.has(key)) oneEntryPerDay.set(key, eventData)
  })

  const pairs = await Promise.all(
    [...oneEntryPerDay].map(async ([key, eventData]) => {
      try {
        return [key, await getTimesheet({ client, eventData, selectedDelegateId })]
      } catch (e) {
        logError(e, 'timeEntryActionHelper > resolveTimesheetsByDay')
        return null
      }
    }),
  )

  return new Map(pairs.filter(Boolean))
}
